import { datadogRum } from '@datadog/browser-rum'
import type {
  Feed,
  FeedEventPayload,
  FeedItem,
  FeedMetadata,
} from '@knocklabs/client'
import type { SeenMedplumClient } from '@seen/core/client'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { InboxLiveSync } from '#src/contexts/InboxLiveSyncContext.ts'
import {
  fetchInboxPage,
  fetchInboxPages,
  findOrphanedTaskItems,
  findSupersededItems,
  type PatchInboxItemsResult,
  patchInboxItems,
  supersedeKeyOf,
  taskIdOf,
} from '#src/hooks/inbox/inboxFeedSync.ts'
import {
  archiveInboxItemsVerified,
  probeArchivedInboxIds,
  removeInboxItems,
} from '#src/hooks/inbox/inboxLiveRemoval.ts'
import {
  applyInboxMetadata,
  fetchInboxMetadata,
} from '#src/hooks/inbox/inboxMetadata.ts'
import {
  broadcastArchivedInboxItems,
  broadcastInboxMetadata,
  broadcastInboxPage,
  subscribeToInboxSync,
  withInboxSyncLock,
} from '#src/hooks/inbox/inboxSyncChannel.ts'
import { KNOCK_PAGE_SIZE } from '#src/hooks/useNotificationItems.ts'

// One coordinator for the whole app, mounted beside the feeds rather than on
// the inbox route. Two reasons it lives up here:
//
// Route-scoped, the listener existed only while the inbox was on screen, so the
// sidebar's unread badge — which is always on screen — went stale the moment
// the user opened a chart, and every clear made anywhere else stayed invisible
// until they navigated back. App-wide, the list and the count track reality
// continuously, from a chart as much as from the inbox.
//
// And there must be exactly one. A Feed's channel name omits its query, so the
// inbox feed and the Done feed share it; a listener mounted per Feed would
// double the work and could write removals into the wrong store. This is bound
// explicitly to the inbox feed and instantiated once.
//
// Every read here is store-free and every write is surgical. Nothing replaces
// the store, so nothing can move the row a reader has open.

// One sweep per tick, on the leader: the archived-only probe (rows cleared by
// the backend or on another device), the first page (read state and
// reappearances), and the closed-task orphans Knock still counts. Thirty
// seconds bounds how long a row cleared elsewhere can linger.
const SWEEP_INTERVAL_MS = 30_000
const METADATA_POLL_INTERVAL_MS = 90_000
// Long enough for the burst of broadcasts one archive produces across N tabs to
// collapse into a single request, short enough that the badge still reads as
// immediate.
const METADATA_COALESCE_MS = 400
// A tick is skipped when a snapshot this fresh already arrived, from this tab or
// any other, so N idle tabs make one request per interval between them rather
// than N. Timestamps are comparable across tabs because BroadcastChannel reaches
// only the same browser on the same machine, hence the same clock.
const METADATA_POLL_FLOOR_MS = METADATA_POLL_INTERVAL_MS * 0.75
const SWEEP_FLOOR_MS = SWEEP_INTERVAL_MS * 0.75
const TASK_LOOKUP_CHUNK = 50
// A superseded message can sit anywhere in the backlog, so the walk that finds
// them has to reach the bottom. Ten pages is 500 messages against a real
// ceiling near 200; the cap only bounds a runaway test account.
const FULL_WALK_MAX_PAGES = 10

export function useInboxLiveSync({
  feedClient,
  medplum,
  enabled,
}: {
  feedClient: Feed
  medplum: SeenMedplumClient
  enabled: boolean
}): InboxLiveSync {
  const feedClientRef = useRef(feedClient)
  feedClientRef.current = feedClient
  const medplumRef = useRef(medplum)
  medplumRef.current = medplum
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  // The row open in a detail panel. Read at apply time, not when a request
  // starts: the user can change selection while one is in flight, and the row
  // that must be protected is whichever is open when the removal lands.
  const protectedItemIdRef = useRef<string | undefined>(undefined)
  // Assertions withheld because their row was open. Held until the selection
  // moves, then applied, so the row leaves on close rather than waiting for the
  // next sweep. Keyed by id, so repeated assertions collapse.
  const withheldRef = useRef<Set<string>>(new Set())
  const lastMetadataAtRef = useRef(0)
  const lastPageAtRef = useRef(0)
  // When this tab last started a sweep. Keyed on the start, not the result, so
  // a remount or a burst of visibility events cannot sweep more than once per
  // floor even when a page comes back empty.
  const lastSweepAtRef = useRef(0)
  const coalesceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )
  // The archived page is the same ids tick after tick; only what is new since
  // the last look is worth applying or telling anyone about.
  const seenArchivedRef = useRef<Set<string>>(new Set())
  // When each id was last asserted archived here. A page read before that
  // moment still lists the row, whichever tab read it; reapplying it would undo
  // an archive the user can already see gone. Pruned as pages are applied.
  const removedAtRef = useRef<Map<string, number>>(new Map())
  // Closed is terminal, so a task found closed is never looked up again.
  const closedTaskIdsRef = useRef<Set<string>>(new Set())
  const sweepInFlightRef = useRef(false)
  // Every live message this tab knows of that belongs to a conversation, by id,
  // from the last full walk plus whatever arrived since. This is what a newly
  // arrived message is checked against, so its older siblings are found even
  // when they sit pages below anything the list has loaded.
  const knownItemsRef = useRef<Map<string, FeedItem>>(new Map())
  // A superseded message the user currently has open. Archiving it would pull
  // the panel out from under them, so it waits until the selection moves.
  const withheldSupersedeRef = useRef<Map<string, FeedItem>>(new Map())
  // Set until this tab has completed one walk of the whole feed. A tab that lost
  // the mount lock has an empty map and would still win a later realtime lock and
  // find nothing to supersede, so the walk is retried on the next tick rather
  // than waiting for the tab to be hidden and shown again.
  const pendingFullWalkRef = useRef(true)

  const forgetKnown = useCallback((itemIds: Iterable<string>) => {
    for (const id of itemIds) {
      knownItemsRef.current.delete(id)
    }
  }, [])

  const rememberKnown = useCallback((entries: FeedItem[]) => {
    for (const entry of entries) {
      if (entry.archived_at) {
        knownItemsRef.current.delete(entry.id)
      } else if (supersedeKeyOf(entry) !== null) {
        knownItemsRef.current.set(entry.id, entry)
      }
    }
  }, [])

  const applyRemovals = useCallback(
    (itemIds: string[]) => {
      if (!enabledRef.current || itemIds.length === 0) {
        return
      }
      forgetKnown(itemIds)
      const now = Date.now()
      for (const id of itemIds) {
        removedAtRef.current.set(id, now)
      }
      const { withheld } = removeInboxItems({
        feedClient: feedClientRef.current,
        itemIds,
        protectedItemId: protectedItemIdRef.current,
      })
      if (withheld !== undefined) {
        withheldRef.current.add(withheld)
      }
    },
    [forgetKnown]
  )

  // Archives every known message that a newer one in its conversation has made
  // redundant. Runs after a walk and on each realtime arrival. The open row is
  // the one exception: held back, and archived when the selection moves.
  const archiveSuperseded = useCallback(async (): Promise<number> => {
    const superseded = findSupersededItems([...knownItemsRef.current.values()])
    const protectedId = protectedItemIdRef.current
    const held = superseded.find((item) => item.id === protectedId)
    if (held) {
      withheldSupersedeRef.current.set(held.id, held)
    }
    const now = superseded.filter((item) => item.id !== protectedId)
    if (now.length === 0) {
      return 0
    }
    // The kill switch is read at the write rather than at the call, because
    // every caller reaches here after an await it can flip during: the sweep's
    // chunked task lookups, the realtime path's wait for the lock.
    if (!enabledRef.current) {
      return 0
    }
    const confirmed = new Set(
      await archiveInboxItemsVerified(
        feedClientRef.current,
        now,
        () => enabledRef.current
      )
    )
    // Only a confirmed archive retires a message. Forgetting on the attempt
    // would drop it from the index with nothing archived, so no later tick
    // would retry it and the older sibling would keep counting in the badge.
    const archived = now.filter((item) => confirmed.has(item.id))
    forgetKnown(archived.map((item) => item.id))
    return archived.length
  }, [forgetKnown])

  const applyMetadata = useCallback(
    (metadata: FeedMetadata, fetchedAt: number) => {
      if (!enabledRef.current || fetchedAt <= lastMetadataAtRef.current) {
        return
      }
      lastMetadataAtRef.current = fetchedAt
      applyInboxMetadata(feedClientRef.current, metadata)
    },
    []
  )

  // The rows of a page that were not asserted archived here after it was read.
  const liveSince = useCallback(
    (entries: FeedItem[], fetchedAt: number): FeedItem[] =>
      entries.filter((entry) => {
        const removedAt = removedAtRef.current.get(entry.id)
        return removedAt === undefined || removedAt < fetchedAt
      }),
    []
  )

  // One path for a page whichever tab read it, so the bookkeeping is the same
  // for the reader and for a follower: a row the feed still lists is not
  // archived any more — un-done here or on another device — so a later archive
  // of it has to count as fresh rather than as one already handled.
  const applyPage = useCallback(
    (entries: FeedItem[], fetchedAt: number): PatchInboxItemsResult => {
      if (!enabledRef.current || fetchedAt <= lastPageAtRef.current) {
        return { updated: 0, reappeared: 0 }
      }
      lastPageAtRef.current = fetchedAt
      const live = liveSince(entries, fetchedAt)
      for (const entry of live) {
        seenArchivedRef.current.delete(entry.id)
      }
      // Pages apply in read order, so nothing older than this one is consulted
      // again.
      for (const [id, removedAt] of removedAtRef.current) {
        if (removedAt < fetchedAt) {
          removedAtRef.current.delete(id)
        }
      }
      return patchInboxItems(feedClientRef.current, live)
    },
    [liveSince]
  )

  // A route-entry refetch replaces the whole store outside this coordinator.
  // Any page read before it finished is older than what is on screen now, and
  // applying it would put back rows that fetch dropped.
  const markStoreReplaced = useCallback((at: number) => {
    if (at > lastPageAtRef.current) {
      lastPageAtRef.current = at
    }
  }, [])

  const requestMetadataRefresh = useCallback(() => {
    if (!enabledRef.current || coalesceTimerRef.current !== undefined) {
      return
    }
    coalesceTimerRef.current = setTimeout(() => {
      coalesceTimerRef.current = undefined
      void withInboxSyncLock(
        feedClientRef.current,
        async () => {
          const fetchedAt = Date.now()
          const metadata = await fetchInboxMetadata(feedClientRef.current)
          // Re-read after the await: a snapshot this tab will not apply must
          // not be broadcast either.
          if (!metadata || !enabledRef.current) {
            return
          }
          applyMetadata(metadata, fetchedAt)
          broadcastInboxMetadata(feedClientRef.current, metadata, fetchedAt)
        },
        'metadata'
      )
    }, METADATA_COALESCE_MS)
  }, [applyMetadata])

  // Archives notifications whose task the server says is closed. The inbox
  // hides those rows but Knock still counts them — the "badge says 2, list is
  // empty" report. Archiving goes through the verified publisher, so the other
  // tabs drop the rows and the badge refreshes from the server.
  const sweepOrphans = useCallback(async (): Promise<{
    tasksChecked: number
    orphansArchived: number
  }> => {
    const client = feedClientRef.current
    const candidates = new Map<string, FeedItem[]>()
    for (const item of client.store.getState().items) {
      const taskId = taskIdOf(item)
      if (taskId === undefined || closedTaskIdsRef.current.has(taskId)) {
        continue
      }
      const group = candidates.get(taskId) ?? []
      group.push(item)
      candidates.set(taskId, group)
    }
    if (candidates.size === 0) {
      return { tasksChecked: 0, orphansArchived: 0 }
    }
    const taskIds = [...candidates.keys()]
    const tasks: StandardTask[] = []
    for (let i = 0; i < taskIds.length; i += TASK_LOOKUP_CHUNK) {
      const chunk = taskIds.slice(i, i + TASK_LOOKUP_CHUNK)
      const found = await medplumRef.current
        .searchResourceClass(StandardTask, {
          _id: chunk.join(','),
          _count: TASK_LOOKUP_CHUNK,
        })
        .catch(() => [])
      tasks.push(...found)
    }
    const items = [...candidates.values()].flat()
    const orphans = findOrphanedTaskItems(items, tasks)
    // Read again for the same reason as the supersede pass: the lookups above
    // take seconds, and the sweep's earlier check is stale by now.
    if (!enabledRef.current) {
      return { tasksChecked: taskIds.length, orphansArchived: 0 }
    }
    const confirmed = new Set(
      await archiveInboxItemsVerified(client, orphans, () => enabledRef.current)
    )
    // Only an archive the server confirmed makes a task terminal. Memoizing on
    // the lookup alone would let one swallowed failure skip that task for the
    // rest of the session, leaving the orphan counting in the badge.
    const archived = orphans.filter((orphan) => confirmed.has(orphan.id))
    // A task with several orphans is terminal only once every one of them is
    // confirmed; marking it on the first would skip a sibling whose archive
    // failed for the rest of the session.
    const tasksStillPending = new Set(
      orphans
        .filter((orphan) => !confirmed.has(orphan.id))
        .map((orphan) => taskIdOf(orphan))
    )
    for (const orphan of archived) {
      const taskId = taskIdOf(orphan)
      if (taskId !== undefined && !tasksStillPending.has(taskId)) {
        closedTaskIdsRef.current.add(taskId)
      }
    }
    forgetKnown(archived.map((orphan) => orphan.id))
    return { tasksChecked: taskIds.length, orphansArchived: archived.length }
  }, [forgetKnown])

  // `full` walks the whole feed instead of the first page. Mount and
  // tab-visible do that, because anything can have changed while nothing was
  // watching; the 30s tick reads page one, where everything new lands.
  const sweep = useCallback(
    (full = false) => {
      if (!enabledRef.current || sweepInFlightRef.current) {
        return
      }
      if (Date.now() - lastSweepAtRef.current < SWEEP_FLOOR_MS) {
        return
      }
      lastSweepAtRef.current = Date.now()
      sweepInFlightRef.current = true
      const walkFull = full || pendingFullWalkRef.current
      void withInboxSyncLock(feedClientRef.current, async () => {
        const client = feedClientRef.current
        const fetchedAt = Date.now()
        // A walk cannot carry a message that arrives after it starts, so the
        // only ids its result may retire are the ones already indexed here.
        const retirable = walkFull
          ? new Set(knownItemsRef.current.keys())
          : undefined
        const [archivedIds, walked] = await Promise.all([
          probeArchivedInboxIds(client).catch(() => undefined),
          walkFull
            ? fetchInboxPages(client, FULL_WALK_MAX_PAGES)
            : fetchInboxPage(client),
        ])
        // The flag is the kill switch, and these reads take seconds. Anything
        // past this point writes, so re-read it rather than trusting the check
        // the sweep started with.
        if (!enabledRef.current) {
          return
        }
        // Pages are consecutive, so the head of a walk is page one.
        const entries = walkFull ? walked?.slice(0, KNOCK_PAGE_SIZE) : walked
        let removed = 0
        const archivedSet = new Set(archivedIds ?? [])
        if (archivedIds) {
          const fresh = archivedIds.filter(
            (id) => !seenArchivedRef.current.has(id)
          )
          for (const id of archivedIds) {
            seenArchivedRef.current.add(id)
          }
          if (fresh.length > 0) {
            removed = fresh.length
            applyRemovals(fresh)
            broadcastArchivedInboxItems(client, fresh)
            requestMetadataRefresh()
          }
        }
        let patched = { updated: 0, reappeared: 0 }
        if (entries) {
          // Probe and page are read in parallel, so a page issued before an
          // archive can still carry the row the probe just removed. Putting it
          // back would strand it: its id is in the seen set now, so no later
          // probe would remove it a second time.
          const live = entries.filter((entry) => !archivedSet.has(entry.id))
          patched = applyPage(live, fetchedAt)
          broadcastInboxPage(client, live, fetchedAt)
        }
        if (walked) {
          if (retirable) {
            const walkedIds = new Set(walked.map((entry) => entry.id))
            for (const id of retirable) {
              if (!walkedIds.has(id)) {
                knownItemsRef.current.delete(id)
              }
            }
            pendingFullWalkRef.current = false
          }
          rememberKnown(
            liveSince(
              walked.filter((entry) => !archivedSet.has(entry.id)),
              fetchedAt
            )
          )
        }
        const orphans = await sweepOrphans()
        const superseded = await archiveSuperseded()
        // Reported only when the sweep changed something, so the volume is the
        // number of corrections rather than one action per tab per 30s. The
        // orphan count is the one that matters: it is the signal that the
        // create-then-complete race is still producing what this exists to
        // clean, and it cannot be reproduced on demand.
        //
        // Datadog query: `@action.name:"inbox.sweep"`
        if (
          removed > 0 ||
          patched.updated > 0 ||
          patched.reappeared > 0 ||
          orphans.orphansArchived > 0 ||
          superseded > 0
        ) {
          datadogRum.addAction('inbox.sweep', {
            removed,
            patched: patched.updated,
            reappeared: patched.reappeared,
            orphans_archived: orphans.orphansArchived,
            tasks_checked: orphans.tasksChecked,
            superseded,
            full: walkFull,
          })
        }
      }).finally(() => {
        sweepInFlightRef.current = false
      })
    },
    [
      applyRemovals,
      applyPage,
      archiveSuperseded,
      rememberKnown,
      requestMetadataRefresh,
      sweepOrphans,
    ]
  )

  const setProtectedItemId = useCallback(
    (itemId: string | undefined) => {
      protectedItemIdRef.current = itemId
      // The flag is the revert mechanism for this surface, so it has to gate the
      // writes too: what was withheld while it was on must not still delete rows
      // and archive messages after it is switched off.
      if (!enabledRef.current) {
        return
      }
      const released = [...withheldSupersedeRef.current.values()].filter(
        (item) => item.id !== itemId
      )
      if (released.length > 0) {
        for (const item of released) {
          withheldSupersedeRef.current.delete(item.id)
        }
        // The successor may have been archived while the row was held, which
        // would leave the held message the only live one in its conversation.
        const pool = new Map(knownItemsRef.current)
        for (const item of released) {
          pool.set(item.id, item)
        }
        const stillSuperseded = new Set(
          findSupersededItems([...pool.values()]).map((item) => item.id)
        )
        const releasedSupersede = released.filter((item) =>
          stillSuperseded.has(item.id)
        )
        if (releasedSupersede.length > 0) {
          void archiveInboxItemsVerified(
            feedClientRef.current,
            releasedSupersede,
            () => enabledRef.current
          ).then(forgetKnown)
        }
      }
      if (withheldRef.current.size === 0) {
        return
      }
      const pending = [...withheldRef.current].filter((id) => id !== itemId)
      if (pending.length === 0) {
        return
      }
      for (const id of pending) {
        withheldRef.current.delete(id)
      }
      removeInboxItems({
        feedClient: feedClientRef.current,
        itemIds: pending,
        protectedItemId: itemId,
      })
    },
    [forgetKnown]
  )

  useEffect(() => {
    if (!enabled) {
      return
    }
    return subscribeToInboxSync(feedClient, {
      onArchived: (itemIds) => {
        for (const id of itemIds) {
          seenArchivedRef.current.add(id)
        }
        applyRemovals(itemIds)
        // The rows are gone locally, but the counts are the server's to state,
        // and they cover items no tab has loaded.
        requestMetadataRefresh()
      },
      onMetadata: applyMetadata,
      onPage: applyPage,
    })
  }, [
    feedClient,
    enabled,
    applyRemovals,
    applyMetadata,
    applyPage,
    requestMetadataRefresh,
  ])

  // A message that just arrived over the socket may make an older one in its
  // conversation redundant, wherever that one sits in the backlog. Checked
  // right away rather than on the next tick, so the badge is right within a
  // second of the socket. Every tab hears the socket; the lock makes one act.
  useEffect(() => {
    if (!enabled) {
      return
    }
    function onRealtime(payload: FeedEventPayload): void {
      if (!enabledRef.current) {
        return
      }
      rememberKnown(payload.items)
      if (!payload.items.some((item) => supersedeKeyOf(item) !== null)) {
        return
      }
      void withInboxSyncLock(feedClientRef.current, async () => {
        const archived = await archiveSuperseded()
        if (archived > 0) {
          // Datadog query: `@action.name:"inbox.supersede"`
          datadogRum.addAction('inbox.supersede', { archived })
        }
      })
    }
    feedClient.on('items.received.realtime', onRealtime)
    return () => {
      feedClient.off('items.received.realtime', onRealtime)
    }
  }, [feedClient, enabled, archiveSuperseded, rememberKnown])

  useEffect(() => {
    if (!enabled) {
      return
    }
    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible') {
        requestMetadataRefresh()
        sweep(true)
      }
    }
    /* Seed at mount. Nothing else does: visibilitychange does not fire for an
       already-visible tab, and the first ticks are 30s and 90s away. */
    if (document.visibilityState === 'visible') {
      requestMetadataRefresh()
      sweep(true)
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    const sweepInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        sweep()
      }
    }, SWEEP_INTERVAL_MS)
    const metadataInterval = setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return
      }
      if (Date.now() - lastMetadataAtRef.current < METADATA_POLL_FLOOR_MS) {
        return
      }
      requestMetadataRefresh()
    }, METADATA_POLL_INTERVAL_MS)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      clearInterval(sweepInterval)
      clearInterval(metadataInterval)
      if (coalesceTimerRef.current !== undefined) {
        clearTimeout(coalesceTimerRef.current)
        coalesceTimerRef.current = undefined
      }
    }
  }, [enabled, requestMetadataRefresh, sweep])

  return useMemo(
    () => ({ setProtectedItemId, markStoreReplaced }),
    [setProtectedItemId, markStoreReplaced]
  )
}
